import React from "react";
import type { RouteObject } from "react-router-dom";

import type { SidoEnum } from "@/api/generated/model";

export type SettingsSection = RouteObject & {
  /** `/settings/<path>` 하위 경로이자 라우트 key. */
  path: string;
  /** 왼쪽 메뉴에 표시할 이름. */
  label: string;
  /** 지연 로드되는 본문 컴포넌트. */
  Component: React.ComponentType;
  /** 있으면 로그인 사용자의 `permissions`에 이 값이 없을 때 메뉴에서 숨긴다. */
  requiredPerm?: string;
};

// 메뉴가 늘어나면 이 배열에 항목만 추가한다 — 사이드 메뉴와 라우트(router.tsx) 둘 다
// 이 목록에서 생성되므로 각 항목을 두 곳에 나눠 적을 필요가 없다.
export const SETTINGS_SECTIONS: SettingsSection[] = [
  {
    path: "account-permission",
    label: "계정 권한",
    Component: React.lazy(() => import("./pages/sections/AccountPermission")),
    requiredPerm: "accounts.view_customuser",
  },
  {
    path: "danger-rate-limit",
    label: "위험 요청 제한",
    Component: React.lazy(
      () => import("./pages/sections/DangerRateLimitSection"),
    ),
  },
  {
    path: "alert-stage-weight",
    label: "경보 단계 가중치",
    Component: React.lazy(() => import("./pages/sections/AlertStageWeight")),
  },
  {
    path: "negative-emotion",
    label: "부정 감정",
    Component: React.lazy(() => import("./pages/sections/NegativeEmotion")),
  },
  {
    path: "negative-ratio-level",
    label: "부정 정서 비율 단계",
    Component: React.lazy(() => import("./pages/sections/NegativeRatioLevel")),
  },
  {
    path: "mental-health-signal",
    label: "정신건강 신호등",
    Component: React.lazy(() => import("./pages/sections/MentalHealthSignal")),
  },
  {
    path: "suicide-risk-signal",
    label: "자살 위험 신호",
    Component: React.lazy(() => import("./pages/sections/SuicideRiskSignal")),
  },
  {
    path: "suicide-trigger-keyword",
    label: "자살 위험 키워드",
    Component: React.lazy(
      () => import("./pages/sections/SuicideTriggerKeyword"),
    ),
    requiredPerm: "risk_keyword.view_riskkeyword",
  },
  {
    path: "channel-sido",
    label: "채널 시·도",
    Component: React.lazy(() => import("./pages/sections/ChannelSido")),
    requiredPerm: "video.view_channel",
  },
  {
    path: "test-2",
    label: "테스트 탭 2",
    Component: React.lazy(() => import("./pages/sections/TestSection2")),
  },
  {
    path: "test-3",
    label: "테스트 탭 3",
    Component: React.lazy(() => import("./pages/sections/TestSection3")),
  },
];

/** 시·도 필터·선택의 "미지정" 옵션 — `Channel.sido` 가 NULL 인 채널. `Select` 는 빈 문자열 value 를 못 받아 sentinel 로 둔다. */
export const SIDO_UNASSIGNED = "unassigned";

/** 시·도 선택지 — 백엔드 `video.constants.SIDO_NAMES` 와 같은 순서(약칭 17개). */
export const SIDO_OPTIONS: SidoEnum[] = [
  "서울",
  "경기",
  "인천",
  "강원",
  "대전",
  "세종",
  "충북",
  "충남",
  "광주",
  "전북",
  "전남",
  "부산",
  "대구",
  "울산",
  "경북",
  "경남",
  "제주",
];

/** `Select` 가 닫혀 있을 때 보여 줄 라벨(값 → 라벨). 시·도는 값과 라벨이 같고 sentinel 만 다르다. */
export const SIDO_SELECT_ITEMS: Record<string, string> = {
  [SIDO_UNASSIGNED]: "미지정",
  ...Object.fromEntries(SIDO_OPTIONS.map((sido) => [sido, sido])),
};

/** 시·도 필터의 "전체" 옵션 sentinel. */
export const SIDO_FILTER_ALL = "all";

/** 시·도 필터 `Select` 의 닫힌 상태 라벨 — 선택용 라벨에 "전체"를 더한 것. */
export const SIDO_FILTER_ITEMS: Record<string, string> = {
  [SIDO_FILTER_ALL]: "전체",
  ...SIDO_SELECT_ITEMS,
};
